/**
 * FP&A: ajuste gerencial de uma linha do Realizado (ADR 0032, seção 10.5). A linha importada não
 * muda; o ajuste guarda o tratamento gerencial (Tipo de Gasto, Classificação, centro ou rateio,
 * descrição) e, explicitamente, diferenças de valor ou competência, sempre com o motivo.
 */
import { z } from 'zod';
import { toScaled } from './money.ts';
import {
  checkAcquisition,
  FPA_ACQUISITION_STATUSES,
  FPA_FREIGHT_PURPOSES,
  fpaPurposeFields,
} from './fpa-aquisicao.ts';

const uuid = z.uuid();
const reason = z.string().trim().min(3, 'Informe o motivo').max(1000);
const amount = z
  .string()
  .trim()
  .regex(/^-?\d{1,15}(\.\d{1,4})?$/, 'Use um número com até 4 casas decimais');

/** Percentual de um destino do rateio (até 4 casas, entre 0 e 100). */
const percent = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Use um percentual com até 4 casas');

export const fpaActualAdjustmentInput = z
  .object({
    expenseTypeId: uuid.nullish().transform((v) => v ?? null),
    classificationId: uuid.nullish().transform((v) => v ?? null),
    /** Outro centro para a linha inteira (da mesma empresa). */
    costCenterId: uuid.nullish().transform((v) => v ?? null),
    /** Ou um rateio entre centros da mesma empresa, somando 100%. */
    allocation: z
      .array(z.object({ costCenterId: uuid, percent }))
      .min(2)
      .max(50)
      .nullish()
      .transform((v) => v ?? null),
    description: z
      .string()
      .trim()
      .max(500)
      .nullish()
      .transform((v) => v || null),
    /** Diferença de valor: o valor gerencial em BRL (o importado continua guardado). */
    amountBrl: amount.nullish().transform((v) => v ?? null),
    /** Diferença de competência: o mês gerencial (AAAA-MM). */
    competence: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])(-01)?$/, 'Use o formato AAAA-MM')
      .nullish()
      .transform((v) => (v ? `${v.slice(0, 7)}-01` : null)),
    reconciliation: z
      .string()
      .trim()
      .max(1000)
      .nullish()
      .transform((v) => v || null),
    // Finalidade do frete e gastos de aquisição (ADR 0032, 13); vazio = sem mudança.
    freightPurpose: z
      .enum(FPA_FREIGHT_PURPOSES)
      .nullish()
      .transform((v) => v ?? null),
    acquisitionStatus: z
      .enum(FPA_ACQUISITION_STATUSES)
      .nullish()
      .transform((v) => v ?? null),
    acquisitionPercent: fpaPurposeFields.acquisitionPercent,
    acquisitionBasis: fpaPurposeFields.acquisitionBasis,
    reason,
  })
  .superRefine((v, ctx) => {
    if (v.acquisitionStatus) {
      checkAcquisition({ ...v, acquisitionStatus: v.acquisitionStatus }, (path, message) =>
        ctx.addIssue({ code: 'custom', message, path: [path] }),
      );
    } else if (v.acquisitionPercent || v.acquisitionBasis) {
      ctx.addIssue({
        code: 'custom',
        message: 'Responda se o gasto afetará o CAC',
        path: ['acquisitionStatus'],
      });
    }
    if (
      !v.freightPurpose &&
      !v.acquisitionStatus &&
      !v.expenseTypeId &&
      !v.classificationId &&
      !v.costCenterId &&
      !v.allocation &&
      !v.description &&
      !v.amountBrl &&
      !v.competence
    ) {
      ctx.addIssue({ code: 'custom', message: 'Nada a ajustar', path: ['reason'] });
    }
    if (v.costCenterId && v.allocation) {
      ctx.addIssue({
        code: 'custom',
        message: 'Escolha um centro ou um rateio, não os dois',
        path: ['allocation'],
      });
    }
    if (v.allocation) {
      const total = v.allocation.reduce((a, x) => a + toScaled(x.percent), 0n);
      if (total !== toScaled('100')) {
        ctx.addIssue({
          code: 'custom',
          message: 'O rateio precisa somar 100%',
          path: ['allocation'],
        });
      }
      if (new Set(v.allocation.map((x) => x.costCenterId)).size !== v.allocation.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'Centro repetido no rateio',
          path: ['allocation'],
        });
      }
    }
    // Valor ou competência diferentes da origem: explícito, com a conciliação.
    if ((v.amountBrl || v.competence) && (!v.reconciliation || v.reconciliation.length < 3)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Explique a conciliação da diferença de valor ou competência',
        path: ['reconciliation'],
      });
    }
  });
export type FpaActualAdjustmentInput = z.infer<typeof fpaActualAdjustmentInput>;

export const fpaActualAdjustmentUndoInput = z.object({ reason });

/** Visão do Realizado na DRE e nas comparações. */
export const FPA_ACTUAL_VIEWS = ['adjusted', 'original'] as const;
export type FpaActualView = (typeof FPA_ACTUAL_VIEWS)[number];

/**
 * Os dados da origem que, se mudarem numa reimportação, tornam o ajuste duvidoso: empresa e
 * centro, nomes originais, conta, valores, moeda, competência e status. Texto canônico (a API faz
 * o hash); a posição na planilha e a descrição não entram.
 */
export function actualFingerprintText(row: {
  competence: string | null;
  costCenterCode: string | null;
  entityLabel: string | null;
  classificationOriginal: string | null;
  expenseTypeOriginal: string | null;
  amountOriginal: string | null;
  currencyOriginal: string | null;
  amountBrl: string | null;
  statusOriginal: string | null;
  original: Record<string, unknown>;
}): string {
  const n = (v: string | null) => (v === null ? '' : toScaled(v).toString());
  return [
    row.competence ?? '',
    row.costCenterCode ?? '',
    row.entityLabel ?? '',
    row.classificationOriginal ?? '',
    row.expenseTypeOriginal ?? '',
    String(row.original['Conta Contábil'] ?? ''),
    n(row.amountOriginal),
    row.currencyOriginal ?? '',
    n(row.amountBrl),
    row.statusOriginal ?? '',
  ].join('|');
}

/** Colunas que dão a chave estável (transação e linha do NetSuite), como no leitor. */
const LINE_COLUMNS = [
  'ID da Linha',
  'Linha',
  'Line ID',
  'Line Unique Key',
  'ID da linha da transação',
];
/** A linha veio com a transação e a linha do NetSuite (chave estável entre exportações). */
export function actualHasStableKey(original: Record<string, unknown>): boolean {
  const filled = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';
  return filled(original['ID']) && LINE_COLUMNS.some((c) => filled(original[c]));
}
